import type { ErrorComponentProps } from "@tanstack/react-router";
import { useEffect } from "react";

import { APP_DISPLAY_NAME } from "../branding";
import { AppTopBarShell } from "../components/AppTopBar";
import { Button } from "../components/ui/button";
import { captureWebError } from "../sentry";
import { rootRouteErrorDetails, rootRouteErrorMessage } from "./-rootRoute.logic";

export function RootRouteErrorView({ error, reset }: ErrorComponentProps) {
  const message = rootRouteErrorMessage(error);
  const details = rootRouteErrorDetails(error);

  useEffect(() => {
    captureWebError(error, { origin: "router.errorComponent" });
  }, [error]);

  return (
    <AppTopBarShell showNavigationControls>
      <div className="relative flex h-full w-full items-center justify-center overflow-hidden bg-background px-4 py-10 text-foreground sm:px-6">
        <div className="pointer-events-none absolute inset-0 opacity-80">
          <div className="absolute inset-x-0 top-0 h-44 bg-[radial-gradient(44rem_16rem_at_top,color-mix(in_srgb,var(--color-red-500)_16%,transparent),transparent)]" />
          <div className="absolute inset-0 bg-[linear-gradient(145deg,color-mix(in_srgb,var(--background)_90%,var(--color-black))_0%,var(--background)_55%)]" />
        </div>

        <section className="relative w-full max-w-xl rounded-2xl border border-border/80 bg-card/90 p-6 shadow-2xl shadow-black/20 backdrop-blur-md sm:p-8">
          <p className="text-[11px] font-semibold tracking-[0.18em] text-muted-foreground uppercase">
            {APP_DISPLAY_NAME}
          </p>
          <h1 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">
            Something went wrong.
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{message}</p>

          <div className="mt-5 flex flex-wrap gap-2">
            <Button size="sm" onClick={() => reset()}>
              Try again
            </Button>
            <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
              Reload app
            </Button>
          </div>

          <details className="group mt-5 overflow-hidden rounded-lg border border-border/70 bg-background/55">
            <summary className="cursor-pointer list-none px-3 py-2 text-xs font-medium text-muted-foreground">
              <span className="group-open:hidden">Show error details</span>
              <span className="hidden group-open:inline">Hide error details</span>
            </summary>
            <pre className="max-h-56 overflow-auto border-t border-border/70 bg-background/80 px-3 py-2 text-xs text-foreground/85">
              {details}
            </pre>
          </details>
        </section>
      </div>
    </AppTopBarShell>
  );
}
